/**
 * resultWarnings — shared helper for surfacing non-fatal AM-facing warnings
 * from a completed workflow's result body.
 *
 * Convention: any TOP-LEVEL result key ending in ``_warning`` whose value is a
 * non-empty string is an actionable caveat the AM should see prominently (e.g.
 * carrier_mortgagee_update's ``service_summary_warning`` → "issue the Service
 * Summary manually in Epic"). These are rendered as an always-visible banner at
 * the top of the instance drawer — OUTSIDE the collapsible Result section — so
 * the AM sees them even when that section is collapsed.
 *
 * Used by both ora (WorkflowDetailDrawer) and the Outlook plugin
 * (InstanceDetailDrawer), plus WorkflowResultSection to exclude the same keys
 * from its JSON tree so a warning never appears twice.
 */

/** A plain object, or null — arrays and primitives are not bodies. */
function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/** The early-exit reason on ONE body, at that body's own top level.
 *
 *  Both halves are required: the flag alone says nothing an AM can read, and a
 *  reason without the flag is a leftover string from a run that finished
 *  normally. */
function readEarlyExit(body: Record<string, unknown>): string | null {
  return body.ended_early &&
    typeof body.early_exit_reason === "string" &&
    body.early_exit_reason.length > 0
    ? body.early_exit_reason
    : null;
}

function collectWarningStrings(
  result: Record<string, unknown> | null | undefined,
): string[] {
  if (!result || typeof result !== "object") return [];
  const out: string[] = [];
  for (const [k, v] of Object.entries(result)) {
    if (k.endsWith("_warning") && typeof v === "string" && v.length > 0) {
      out.push(v);
    }
  }
  return out;
}

/** Extract the non-empty ``*_warning`` string values from a result body.

    Hosts fetch the result at two nesting depths (the persist envelope
    `{result, completed_at}` vs the inner object). Same dual-read as
    ``extractManualClear``. */
export function extractResultWarnings(
  result: Record<string, unknown> | null | undefined,
): string[] {
  if (!result || typeof result !== "object") return [];
  const nested = result.result;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const fromNested = collectWarningStrings(
      nested as Record<string, unknown>,
    );
    if (fromNested.length > 0) return fromNested;
  }
  return collectWarningStrings(result);
}

/** Warning strings published on get_workflow_status (`result_warnings`). */
export function warningsFromStatus(
  status: { result_warnings?: unknown } | null | undefined,
): string[] {
  const raw = status?.result_warnings;
  if (!Array.isArray(raw)) return [];
  return raw.filter((w): w is string => typeof w === "string" && w.length > 0);
}

/** True when ``key`` is a ``*_warning`` key (used to filter the JSON tree). */
export function isWarningKey(key: string): boolean {
  return key.endsWith("_warning");
}

/**
 * Extract the first-class early-exit reason from a result body.
 *
 * Orchestrations set ``ended_early: true`` + ``early_exit_reason`` when a run
 * terminates without doing the work (e.g. the safe-rerun guard found the
 * change already completed in Epic, a rejection, a timeout). A run like that
 * must not read as a clean success — both ora (WorkflowDetailDrawer) and the
 * Outlook plugin (InstanceDetailDrawer) render the reason as an
 * "Ended early — <reason>" banner at the very top of the drawer.
 */
export function extractEarlyExitReason(
  result: Record<string, unknown> | null | undefined,
): string | null {
  if (!result || typeof result !== "object") return null;
  // Dual-depth, same as extractResultWarnings / extractManualClear above:
  // hosts hold this body at two nesting depths (the persist envelope
  // `{result, completed_at}` vs the inner object already unwrapped). This
  // helper read the TOP LEVEL ONLY while both siblings dual-read, so a caller
  // holding the envelope got null and showed no banner. Inner first — the
  // more specific shape — then the top level, which also covers the status
  // payload, where the substrate publishes both fields flat.
  const nested = obj(result.result);
  if (nested) {
    const fromNested = readEarlyExit(nested);
    if (fromNested) return fromNested;
  }
  return readEarlyExit(result);
}

/**
 * The three closed-set dispositions an AM can pick when clearing a parked
 * pending_email_send run (email-send-complete-override) — one definition,
 * shared by the clear control (PendingEmailSendDrawerBody) and the
 * after-the-fact attribution on the completed body, so the label an AM
 * picks and the label shown back to them can never drift apart.
 */
export const CLEAR_DISPOSITIONS: { value: string; label: string }[] = [
  { value: "reply_sent_outside_workflow", label: "Reply was sent outside this workflow" },
  { value: "handled_without_reply", label: "Handled without sending a reply" },
  { value: "no_reply_needed", label: "No reply was needed" },
];

/** The AM-facing label for a clear disposition value, falling back to the
 *  raw value itself for a disposition string this UI doesn't recognise
 *  (e.g. a future addition) rather than rendering blank. */
export function clearDispositionLabel(disposition: string): string {
  return (
    CLEAR_DISPOSITIONS.find((d) => d.value === disposition)?.label ?? disposition
  );
}

/** The manual-clear attribution record (email-send-complete-override). */
export interface ManualClear {
  actor_oid: string | null;
  actor_upn: string | null;
  disposition: string;
  note: string | null;
  cleared_at: string | null;
}

function asManualClear(value: unknown): ManualClear | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.disposition !== "string" || v.disposition.length === 0) return null;
  return {
    actor_oid: typeof v.actor_oid === "string" ? v.actor_oid : null,
    actor_upn: typeof v.actor_upn === "string" ? v.actor_upn : null,
    disposition: v.disposition,
    note: typeof v.note === "string" ? v.note : null,
    cleared_at: typeof v.cleared_at === "string" ? v.cleared_at : null,
  };
}

/**
 * Extract the manual-clear attribution stamped by
 * ``manual_override_instance`` (email-send-complete-override, Phase 1) —
 * "afterwards the run shows who cleared it, when, and the reason picked"
 * (PRD AC-2's human-validation half).
 *
 * ``jsonb_set`` writes the record at the path ``{result,manual_clear}``, but
 * the two drawers fetch the result at DIFFERENT nesting depths depending on
 * the read path (the outer ``{result, completed_at}`` envelope in one case,
 * the inner result object directly in the other) — so this checks BOTH
 * ``body.manual_clear`` and the nested ``body.result.manual_clear``, inner
 * first (the more specific / more common shape once unwrapped).
 */
export function extractManualClear(
  body: Record<string, unknown> | null | undefined,
): ManualClear | null {
  if (!body || typeof body !== "object") return null;
  const nested = body.result;
  if (nested && typeof nested === "object") {
    const fromNested = asManualClear((nested as Record<string, unknown>).manual_clear);
    if (fromNested) return fromNested;
  }
  return asManualClear(body.manual_clear);
}
